/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {
    Box,
    Button,
    ModalActions,
    NotificationActions,
    NotificationContainer,
    ProgressIndicator,
    SecurityHelper,
    Stack,
    StyledModal,
    useAssetImages,
    useFlags,
    zipCodeValidator,
} from 'sarsaparilla';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';
import { useRecaptchaWithFallback, RecaptchaCheckbox } from 'shared-ui/recaptcha';

import LEGACY_PassSelectionPage from './LEGACY_PassSelectionPage';
import PassOrderDetails from '../components/PassOrderDetails';
import { NonResidentFeeATBModal } from '../components/NonResidentFeeATBModal';
import {
    clearValidationErrors,
    createReservations,
    deleteLocalReservation,
    fetchFacilityByIdOrSlug,
    fetchPassTypes,
    fetchReservations,
    removeReservation,
    validateReservation as validateReservationAction,
} from '../actions';
import {
    facilityByIdOrSlugSelectors,
    isLoaded,
    isLoading,
    passTypesSelectors,
    reservationsSelectors,
} from '../reducers';
import { getParentAsset, getPrice } from '../api';
import { RECAPTCHA_ERROR, RECAPTCHA_ERROR2 } from '../constants/recaptcha';
import { isDailyPassType } from '../utils/date_time_utils';
import { showNewSitePassLayout as showNewSitePassLayoutUtil } from '../utils/launchDarklyUtils';

function PassOrderDetailsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fidParam = searchParams.get('fid');
    const ridParam = searchParams.get('rid');
    const dispatch = useDispatch();
    const [passOrderDetailsPageState, setPassOrderDetailsPageState] = useState({
        reservation: null,
        parentAsset: null,
        submitAttempted: false,
        isSubmitting: false,
    });
    const [showNonResidentModal, setShowNonResidentModal] = useState(false);
    const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
    const {
        getToken,
        handleRecaptchaError,
        isChallengeRequired,
        isV2Invalid,
        recaptchaCheckboxProps,
        resetChallenge,
    } = useRecaptchaWithFallback();
    const cartReservations = useSelector((state) => state.cart.reservations);
    const reservations = useSelector(reservationsSelectors.getReservations);
    const params = useParams();
    const { siteId, resId } = params;
    const facility = useSelector(facilityByIdOrSlugSelectors.getFacilityByIdOrSlug);
    const loaded = useSelector((state) =>
        isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
    );
    const loading = useSelector((state) =>
        isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
    );
    const passTypes = useSelector((state) => passTypesSelectors.getPassTypes(state));
    const hasNonResidentFees = useMemo(() => {
        return passTypes?.some((passType) => passType?.features?.non_resident_fee);
    }, [passTypes]);
    const localReservation = useSelector(
        (state) => state.parkpass.localReservation.reservation
    );
    const sitePassImages = useAssetImages({ assetId: facility?.asset_id });
    const steps = ['Order Details', 'Shopping Cart', 'Payment', 'Confirmation'];
    const reservationStateRef = useRef(passOrderDetailsPageState?.reservation);

    const isNPSAgency = useMemo(() => facility?.agency === 'NPS', [facility?.agency]);
    const isNewReservation = useMemo(
        () => window.location.pathname.includes('/create/reservation'),
        []
    );
    const isLoggedIn = useMemo(() => {
        return SecurityHelper.isLoggedIn() || SecurityHelper.isGuest();
    }, []);
    const recArea = useMemo(
        () =>
            passOrderDetailsPageState?.parentAsset?.find(
                (item) => item?.entity_id === facility?.asset_id
            ),
        [facility?.asset_id, passOrderDetailsPageState?.parentAsset]
    );
    const facilityIdParam = useMemo(() => {
        return siteId || fidParam;
    }, [fidParam, siteId]);

    const reservationIdParam = useMemo(() => {
        return resId || ridParam;
    }, [ridParam, resId]);

    const filteredPassTypes = useMemo(() => {
        const hasPassTypes = !!passTypes?.length;

        if (!hasPassTypes) {
            return passTypes;
        }

        const newPassTypes = passTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return newPassTypes;
    }, [passTypes]);

    const reservation = useMemo(() => {
        if (isNewReservation) {
            return localReservation;
        }

        return reservations?.[0];
    }, [isNewReservation, localReservation, reservations]);

    const passType = useMemo(() => {
        return filteredPassTypes?.find(({ id }) => id === reservation?.pass_type_id);
    }, [filteredPassTypes, reservation?.pass_type_id]);

    const isNonCommercialPass = !passType?.features?.['is-commercial-pass'];

    const sitePassImage = useMemo(() => {
        return (
            sitePassImages?.heroImage?.url ||
            sitePassImages?.previewImage?.url ||
            sitePassImages?.galleryImages?.[0]?.url
        );
    }, [
        sitePassImages?.galleryImages,
        sitePassImages?.heroImage?.url,
        sitePassImages?.previewImage?.url,
    ]);

    const isNeedToKnowChecked = useMemo(() => {
        return !!passOrderDetailsPageState?.reservation?.pass_info?.features?.accepted
            ?.need_to_know;
    }, [
        passOrderDetailsPageState?.reservation?.pass_info?.features?.accepted
            ?.need_to_know,
    ]);

    const isDataLoading = useMemo(() => {
        return loading.facility || loading.passTypes || loading.reservations;
    }, [loading.facility, loading.passTypes, loading.reservations]);

    const getFacility = useCallback(() => {
        if (!loading.facility && !loaded.facility) {
            dispatch(fetchFacilityByIdOrSlug(facilityIdParam));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityIdParam, loaded.facility, loading.facility]);

    const getParentAssetData = useCallback(async () => {
        if (facility?.asset_id) {
            const { data: parentAssetData } = await getParentAsset(facility?.asset_id);
            setPassOrderDetailsPageState((prevState) => ({
                ...prevState,
                parentAsset: parentAssetData?.results,
            }));
        }
    }, [facility?.asset_id]);

    const getPassTypes = useCallback(() => {
        if (facility?.id && !loading.passTypes && !loaded.passTypes) {
            dispatch(fetchPassTypes(facility?.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facility?.id, loaded.passTypes, loading.passTypes]);

    const getReservation = useCallback(() => {
        if (
            !isNewReservation &&
            reservationIdParam &&
            !loading.reservations &&
            !loaded.reservations
        ) {
            dispatch(fetchReservations([reservationIdParam]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNewReservation, loaded.reservations, loading.reservations, reservationIdParam]);

    const getInitialData = useCallback(() => {
        getFacility();
        getReservation();
    }, [getFacility, getReservation]);

    const redirectToSitePassPage = useCallback(() => {
        navigate(`/sitepass/${facilityIdParam}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityIdParam]);

    useEffect(() => {
        if (!isLoggedIn) {
            redirectToSitePassPage();
        }
    }, [isLoggedIn, redirectToSitePassPage]);

    useEffect(() => {
        // Redirects to sitePass page when the user is not logged in yet
        if (isLoggedIn) {
            getInitialData();
        }

        return () => {
            // delete local reservation when we navigate to another route
            dispatch(deleteLocalReservation());
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getParentAssetData();
    }, [getParentAssetData]);

    useEffect(() => {
        getPassTypes();
    }, [getPassTypes]);

    useEffect(() => {
        // Redirects to sitePass page when the reservation is empty or We get an error
        if (
            (isNewReservation && !localReservation) ||
            (!isNewReservation &&
                loaded?.reservations &&
                (!reservation?.id || reservation?.error))
        ) {
            redirectToSitePassPage();
        }
    }, [
        isNewReservation,
        loaded?.reservations,
        localReservation,
        redirectToSitePassPage,
        reservation?.error,
        reservation?.id,
    ]);

    useEffect(() => {
        if (reservation) {
            const clonedReservation = cloneDeep(reservation);
            if (clonedReservation?.pass_info?.holder_info?.email) {
                clonedReservation.pass_info.holder_info.email_confirmation =
                    clonedReservation?.pass_info?.holder_info?.email;
            }
            setPassOrderDetailsPageState((prevState) => {
                const { has_price, fees, price, total } = prevState?.reservation || {};
                return {
                    ...prevState,
                    reservation: {
                        ...clonedReservation,
                        ...(has_price ? { has_price, fees, price, total } : {}),
                    },
                };
            });
        }
    }, [reservation]);

    useEffect(() => {
        reservationStateRef.current = passOrderDetailsPageState?.reservation;
    });

    const getPassPrice = useCallback(
        async ({ pass_type_id, features, pass_info: { start_date: startDate } }) => {
            let passPrice = null;
            try {
                if (!isDailyPassType(passType?.duration) || !isEmpty(startDate)) {
                    const { data: price } = await getPrice({
                        pass_type_id,
                        features: {
                            ...features,
                            'start-date': startDate,
                        },
                    });

                    passPrice = price;
                }

                return passPrice;
            } catch (error) {
                return passPrice;
            }
        },
        [passType?.duration]
    );

    const handleNeedToKnowChange = useCallback(() => {
        setPassOrderDetailsPageState((prevState) => {
            if (!prevState?.reservation) return prevState;
            const clonedReservation = cloneDeep(prevState.reservation);
            if (!clonedReservation.pass_info.features) {
                clonedReservation.pass_info.features = {};
            }
            if (!clonedReservation.pass_info.features.accepted) {
                clonedReservation.pass_info.features.accepted = {};
            }
            clonedReservation.pass_info.features.accepted.need_to_know =
                !isNeedToKnowChecked;
            return {
                ...prevState,
                reservation: clonedReservation,
            };
        });
    }, [isNeedToKnowChecked]);

    const handleReservationChange = useCallback(
        async (updatedReservation) => {
            const updatedReservationCopy = cloneDeep(updatedReservation);
            const stateReservation = reservationStateRef.current;
            let priceData = {};

            const getPriceRelevantFields = (field) => ({
                'start-date': field?.['start-date'],
                'end-date': field?.['end-date'],
                'day-segment': field?.['day-segment'],
                'attendee-info': field?.['attendee-info'],
                non_resident_fee: field?.non_resident_fee,
            });

            const stateFeatures = getPriceRelevantFields(stateReservation?.features);
            const incomingFeatures = getPriceRelevantFields(
                updatedReservationCopy?.features
            );

            const discountWasRemoved =
                !!stateReservation?.features?.discount?.pass_number &&
                !updatedReservationCopy?.features?.discount?.pass_number;

            if (!isEqual(stateFeatures, incomingFeatures) || discountWasRemoved) {
                const passPrice = await getPassPrice(updatedReservationCopy);
                if (passPrice) {
                    const { fees, price, total } = passPrice;
                    priceData = {
                        has_price: true,
                        fees,
                        price,
                        total,
                    };
                }
            }
            setPassOrderDetailsPageState((prevState) => ({
                ...prevState,
                reservation: {
                    ...updatedReservationCopy,
                    ...priceData,
                    pass_info: {
                        ...updatedReservationCopy?.pass_info,
                        holder_info: updatedReservationCopy?.holder_info,
                        features: updatedReservationCopy?.features,
                    },
                },
            }));
        },
        [getPassPrice]
    );

    const handleDiscountBlur = useCallback(async () => {
        const stateReservation = reservationStateRef.current;
        if (!stateReservation) return;

        const passPrice = await getPassPrice(stateReservation);
        if (passPrice) {
            const { fees, price, total } = passPrice;
            setPassOrderDetailsPageState((prevState) => ({
                ...prevState,
                reservation: {
                    ...prevState.reservation,
                    has_price: true,
                    fees,
                    price,
                    total,
                },
            }));
        }
    }, [getPassPrice]);

    const handleRemoveReservation = useCallback(
        async (id) => {
            if (reservation?.id === id) {
                // Remove any validation errors
                dispatch(clearValidationErrors(0));

                if (isNewReservation) {
                    redirectToSitePassPage();
                    return;
                }

                // Remove on the server
                try {
                    await dispatch(removeReservation(id));
                    window.location.assign('/cart');
                } catch (e) {
                    // No-op. Errors will propagate through props.errors
                    window.scrollTo(0, 0);
                }
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isNewReservation, redirectToSitePassPage, reservation?.id]
    );

    const validateReservation = useCallback(async () => {
        let isValid = false;
        const stateReservation = passOrderDetailsPageState?.reservation;
        if (stateReservation) {
            try {
                const reservationCopy = cloneDeep(stateReservation);
                reservationCopy.holder_info = reservationCopy?.pass_info?.holder_info;
                reservationCopy.features = reservationCopy?.pass_info?.features;
                // We need to set this value here to prevent some issues on BE side
                if (!reservationCopy.features) {
                    reservationCopy.features = {};
                }
                if (!reservationCopy.features.accepted) {
                    reservationCopy.features.accepted = {};
                }
                reservationCopy.features.accepted.need_to_know = true;
                const error = await dispatch(
                    validateReservationAction(0, reservationCopy)
                );

                isValid = !error;
            } catch (error) {
                return isValid;
            }
        }

        return isValid;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passOrderDetailsPageState?.reservation]);

    const validateOtherFields = useCallback(() => {
        const stateReservation = passOrderDetailsPageState?.reservation;

        // Validate that the non resident question is answered for site passes
        // where the pass type has non resident fees
        if (
            passType?.features?.non_resident_fee?.fee &&
            !stateReservation?.features?.non_resident_fee?.answered
        ) {
            return false;
        }

        // Validate that the zip code is a US based zip code for annual passes
        // for facilities that have active non resident fees
        if (
            hasNonResidentFees &&
            stateReservation.pass_info.duration === 'annual' &&
            !zipCodeValidator(stateReservation?.holder_info?.zip_code).isValid
        ) {
            return false;
        }

        return !!(
            stateReservation?.holder_info?.email_confirmation &&
            stateReservation?.holder_info?.email ===
                stateReservation?.holder_info?.email_confirmation &&
            stateReservation?.holder_info?.zip_code &&
            isNeedToKnowChecked
        );
    }, [
        isNeedToKnowChecked,
        passOrderDetailsPageState?.reservation,
        hasNonResidentFees,
        passType,
    ]);

    const proceedWithSave = useCallback(async () => {
        try {
            if (!validateOtherFields()) {
                setPassOrderDetailsPageState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                }));
                throw new Error();
            }

            if (isChallengeRequired && !isChallengeModalOpen) {
                setIsChallengeModalOpen(true);
                return;
            }

            let recaptchaToken = null;
            try {
                recaptchaToken = await getToken('create_sitepass_reservation');
            } catch {
                recaptchaToken = null;
            }

            const recaptchaOptions = {
                recaptcha_token: recaptchaToken,
                recaptcha_version: isChallengeRequired ? 'V2' : 'V3',
            };

            await dispatch(
                createReservations(
                    [passOrderDetailsPageState?.reservation],
                    recaptchaOptions
                )
            );

            // Reservation succeeded, so tear down any active challenge fallback.
            if (isChallengeModalOpen || isChallengeRequired) {
                resetChallenge();
            }
            setIsChallengeModalOpen(false);
            window.location.assign('/cart');
        } catch (error) {
            const { shouldRetry, isFailed } = handleRecaptchaError(error);

            // The invisible/score assessment failed and the service is asking
            // for the visible checkbox challenge. handleRecaptchaError has
            // already flipped the hook into v2 mode; open the fallback modal
            // instead of surfacing an error. Solving it re-submits with a
            // V2 token.
            if (shouldRetry) {
                setIsChallengeModalOpen(true);
                return;
            }

            // reCAPTCHA verification hard-failed. Surface a distinct message
            // depending on which step failed: RECAPTCHA_ERROR2 when the user's
            // checkbox challenge (V2) submission was rejected, otherwise
            // RECAPTCHA_ERROR for the invisible/enterprise assessment failure.
            if (isFailed) {
                const failedCheckbox = isChallengeRequired;
                resetChallenge();
                setIsChallengeModalOpen(false);
                setPassOrderDetailsPageState((prevState) => ({
                    ...prevState,
                    submitAttempted: true,
                    isSubmitting: false,
                }));
                dispatch(
                    NotificationActions.setNotification(
                        'error',
                        failedCheckbox ? RECAPTCHA_ERROR2 : RECAPTCHA_ERROR
                    )
                );
                window.scrollTo(0, 0);
                return;
            }

            setPassOrderDetailsPageState((prevState) => ({
                ...prevState,
                submitAttempted: true,
                isSubmitting: false,
            }));
            validateReservation();
            window.scrollTo(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        passOrderDetailsPageState?.reservation,
        validateOtherFields,
        validateReservation,
        isChallengeRequired,
        isChallengeModalOpen,
        getToken,
        handleRecaptchaError,
        resetChallenge,
        dispatch,
    ]);

    const handleSaveReservation = useCallback(async () => {
        const isValid = await validateReservation();
        if (!isValid) {
            setPassOrderDetailsPageState((prevState) => ({
                ...prevState,
                submitAttempted: true,
                isSubmitting: false,
            }));
            window.scrollTo(0, 0);
            return;
        }

        // Check if total cost (reservation + non-resident fee) exceeds threshold
        const reservationTotal = passOrderDetailsPageState?.reservation?.total || 0;

        let totalCost = 0;
        //the reservation total cost is in cart service format
        if (reservationTotal > 0) {
            totalCost = reservationTotal / 100_000;
        }

        const nonResidentQuantity =
            passOrderDetailsPageState?.reservation?.features?.non_resident_fee
                ?.quantity || 0;

        //Only if the user selected a non-resident fee and the total cost is above $200, we show the modal.  Shown only for non-commercial passes.
        if (totalCost > 200 && nonResidentQuantity > 0 && isNonCommercialPass) {
            setShowNonResidentModal(true);
            return;
        }

        // Continue with original save logic
        await proceedWithSave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [passOrderDetailsPageState?.reservation, validateReservation]);

    const handleModalContinue = useCallback(async () => {
        setShowNonResidentModal(false);
        await proceedWithSave();
    }, [proceedWithSave]);

    const handleModalLearnMore = useCallback(async () => {
        setShowNonResidentModal(false);
        //Clear cart service
        if (!isNewReservation && reservation?.id) {
            dispatch(clearValidationErrors(0));
            await dispatch(removeReservation(reservation.id));
        }

        window.location.assign('/pass');
    }, [isNewReservation, reservation?.id, dispatch]);

    const handleModalClose = useCallback(() => {
        setShowNonResidentModal(false);
    }, []);

    // TBD - We could remove this later if needed
    if (!isNPSAgency) {
        return <LEGACY_PassSelectionPage />;
    }

    return (
        <main className="pass-order-details-page">
            <NotificationContainer className="mb-0" />
            <Stack space="lg" className="pass-order-details-page-container">
                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator
                        eventToReportToGA={gaReportingCartReservationsForStepper(
                            cartReservations,
                            steps,
                            0
                        )}
                        shouldReportToGA
                        steps={steps}
                        activeStep={1}
                    />
                </Box>
                <PassOrderDetails
                    facility={facility}
                    facilityId={facilityIdParam}
                    reservation={passOrderDetailsPageState?.reservation}
                    recArea={recArea}
                    sitePassImage={sitePassImage}
                    passTypes={filteredPassTypes}
                    passType={passType}
                    submitAttempted={passOrderDetailsPageState?.submitAttempted}
                    isLoading={isDataLoading}
                    isNewReservation={isNewReservation}
                    onNeedToKnowChange={handleNeedToKnowChange}
                    onReservationChange={handleReservationChange}
                    onDiscountBlur={handleDiscountBlur}
                    onRemoveReservation={handleRemoveReservation}
                    onSaveReservation={handleSaveReservation}
                />
            </Stack>
            {isNonCommercialPass && (
                <NonResidentFeeATBModal
                    isOpen={showNonResidentModal}
                    onClose={handleModalClose}
                    onContinue={handleModalContinue}
                    onLearnMore={handleModalLearnMore}
                />
            )}
            {isChallengeModalOpen && (
                <StyledModal
                    isOpen={isChallengeModalOpen}
                    heading="We need to verify that you're a human"
                    shouldCloseOnOverlayClick={false}
                    shouldShowCloseButton={false}
                >
                    <RecaptchaCheckbox
                        {...recaptchaCheckboxProps}
                        isFallbackOnly
                        action="create_sitepass_reservation"
                    />
                    <ModalActions>
                        <Button
                            onClick={() => proceedWithSave()}
                            isDisabled={isV2Invalid}
                            gaTrackingId="sitepass-recaptcha-submit"
                            appearance="tertiary"
                        >
                            Continue Booking
                        </Button>
                    </ModalActions>
                </StyledModal>
            )}
        </main>
    );
}

function PassOrderDetailsPageWrapper(props) {
    const { sitePassUiRevamp } = useFlags();
    const shouldShowNewSitePassLayout = useMemo(() => {
        return showNewSitePassLayoutUtil(sitePassUiRevamp);
    }, [sitePassUiRevamp]);

    if (!shouldShowNewSitePassLayout) {
        // eslint-disable-next-line react/jsx-props-no-spreading
        return <LEGACY_PassSelectionPage {...props} />;
    }

    return <PassOrderDetailsPage />;
}

export default PassOrderDetailsPageWrapper;
